import React, { useRef, useState } from 'react';
import { BaseButton, useT } from '@repo/common';
import { ReactComponent as NavigationLeftIcon20 } from '@repo/common/assets/icons/20px/icon_20px_navigation-left.svg';
import { ReactComponent as NavigationRightIcon20 } from '@repo/common/assets/icons/20px/icon_20px_navigation-right.svg';

const MAX_THUMBNAIL_COUNT = 10;
const DRAG_THRESHOLD = 5;

const ImageGallery = ({ images = [] }) => {
  const t = useT();
  const heroNamsan = `${process.env.REACT_APP_ASSETS_URL}/Kpathport/img-Hero-namsan.svg`;
  const heroFlavor = `${process.env.REACT_APP_ASSETS_URL}/Kpathport/img-Hero-flavor.svg`;
  const heroPlay = `${process.env.REACT_APP_ASSETS_URL}/Kpathport/img-Hero-playofmonth.svg`;

  const [currentIndex, setCurrentIndex] = useState(0);
  const thumbnailsRef = useRef(null);
  const dragStateRef = useRef(null);

  const defaultImages = [heroNamsan, heroFlavor, heroPlay];

  const displayImages = images.length > 0 ? images : defaultImages;
  const thumbnails = displayImages.slice(0, MAX_THUMBNAIL_COUNT);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : displayImages.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < displayImages.length - 1 ? prev + 1 : 0));
  };

  const handleThumbnailClick = (index) => {
    // Ignore the click that ends a horizontal drag of the thumbnail strip.
    if (dragStateRef.current?.moved) return;
    setCurrentIndex(index);
  };

  const handleThumbnailsPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || !thumbnailsRef.current) return;
    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: thumbnailsRef.current.scrollLeft,
      moved: false,
    };
  };

  const handleThumbnailsPointerMove = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    const distance = event.clientX - dragState.startX;
    if (!dragState.moved) {
      if (Math.abs(distance) < DRAG_THRESHOLD) return;
      dragState.moved = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    thumbnailsRef.current.scrollLeft = dragState.startScrollLeft - distance;
  };

  const handleThumbnailsPointerUp = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    // Cleared after the click handler runs so a drag never changes the slide.
    setTimeout(() => {
      dragStateRef.current = null;
    }, 0);
  };

  return (
    <>
      {/* Main Image */}
      <div className='relative w-full aspect-square rounded-[30px] overflow-hidden shrink-0 tabletH:h-[718px] tabletH:aspect-auto tabletH:rounded-[50px]'>
        <div
          aria-hidden='true'
          className='absolute inset-0 bg-grayscale-gray01'
        />
        <div className='absolute inset-0'>
          <div
            className='flex h-full transition-transform duration-500 ease-in-out'
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {displayImages.map((image, index) => (
              <div key={`${image}-${index}`} className='h-full w-full shrink-0'>
                <img
                  alt=''
                  loading='lazy'
                  decoding='async'
                  className='h-full w-full object-center object-cover'
                  src={image}
                />
              </div>
            ))}
          </div>
        </div>
        <div className='absolute inset-0 flex items-center justify-between px-[8px] tabletH:px-[16px]'>
          <BaseButton
            type='text'
            size='s'
            className='backdrop-blur-[7.5px] backdrop-filter h-auto min-w-0 border-0 bg-transparent hover:bg-transparent p-[8px] relative rounded-[50px] shrink-0 z-10'
            style={{ backgroundColor: 'rgba(249, 247, 244, 0.6)' }}
            onClick={handlePrev}
            aria-label={t('Previous image', '이전 이미지')}
            icon={
              <div className='flex items-center overflow-clip relative shrink-0 size-[20px]'>
                <NavigationLeftIcon20 className='w-full h-full' />
              </div>
            }
          />
          <BaseButton
            type='text'
            size='s'
            className='backdrop-blur-[7.5px] backdrop-filter h-auto min-w-0 border-0 bg-transparent hover:bg-transparent p-[8px] relative rounded-[50px] shrink-0 z-10'
            style={{ backgroundColor: 'rgba(249, 247, 244, 0.6)' }}
            onClick={handleNext}
            aria-label={t('Next image', '다음 이미지')}
            icon={
              <div className='flex items-center overflow-clip relative shrink-0 size-[20px]'>
                <NavigationRightIcon20 className='w-full h-full' />
              </div>
            }
          />
        </div>
      </div>

      {/* Thumbnails */}
      <div
        ref={thumbnailsRef}
        onPointerDown={handleThumbnailsPointerDown}
        onPointerMove={handleThumbnailsPointerMove}
        onPointerUp={handleThumbnailsPointerUp}
        onPointerCancel={handleThumbnailsPointerUp}
        className='flex gap-[8px] items-center relative w-full shrink-0 overflow-x-auto overflow-y-hidden overscroll-x-contain touch-pan-x select-none scrollbar-hide cursor-grab active:cursor-grabbing tabletH:gap-[16px]'
      >
        {thumbnails.map((thumb, index) => (
          <div
            key={index}
            className='relative rounded-[8px] shrink-0 size-[60px] cursor-pointer tabletH:rounded-[20px] tabletH:size-[120px]'
            onClick={() => handleThumbnailClick(index)}
          >
            <div
              aria-hidden='true'
              className='absolute inset-0 pointer-events-none rounded-[8px] tabletH:rounded-[20px] overflow-hidden'
            >
              <div className='absolute bg-grayscale-gray01 inset-0 rounded-[8px] tabletH:rounded-[20px]' />
              <img
                alt=''
                loading='lazy'
                decoding='async'
                draggable={false}
                className='absolute max-w-none object-center object-cover rounded-[8px] tabletH:rounded-[20px] size-full'
                src={thumb}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default ImageGallery;
